import { createContext, FC, ReactNode, useContext, useMemo } from "react";

import Loading from "src/components/DataStates/Loading.tsx";
import InlineError from "src/components/Errors/InlineError.tsx";
import { DynamicViewSchema } from "src/dynamic-view-renderer/DynamicViewSchemaProvider/DynamicViewSchemaProvider.types.ts";
import { useGetUISchema } from "src/dynamic-view-renderer/DynamicViewSchemaProvider/useGetUISchema.ts";
import { useT } from "src/internationalization";

export type DynamicViewSchemaContextType = DynamicViewSchema;
const DynamicViewSchemaContextInitialState: DynamicViewSchemaContextType = {
  alertPdfDefinition: {
    caseTypes: {},
  },
  caseTypeDefinitions: [
    {
      code: "Transaction",
      label: "Transaction",
      tag: "purple",
      caseListTable: {
        columns: [
          { headerLabel: "ID", headlessWidgetCode: "caseId", width: 1, sorter: true },
          { headerLabel: "Date", headlessWidgetCode: "caseDate", width: 1, sorter: true },
          { headerLabel: "Montant", headlessWidgetCode: "caseAmount", width: 1, sorter: true },
          {
            headerLabel: "Représentant légal",
            headlessWidgetCode: "legalRepresentative",
            width: 100,
          },
          { headerLabel: "Expert Comptable", headlessWidgetCode: "accountant", width: 100 },
          { headerLabel: "Entreprise", headlessWidgetCode: "company", width: 100 },
        ],
        searchPlaceholder: undefined,
      },
    },
    {
      code: "DemandesBel",
      label: "Demandes BEL",
      tag: "teal",
      caseListTable: {
        columns: [
          { headerLabel: "ID", headlessWidgetCode: "caseId", width: 1, sorter: true },
          { headerLabel: "Date", headlessWidgetCode: "caseDate", width: 1, sorter: true },
          { headerLabel: "Région", headlessWidgetCode: "caseRegion", width: 1 },
          { headerLabel: "Étape", headlessWidgetCode: "caseEtape", width: 1 },
        ],
      },
    },
    {
      code: "Demande",
      label: "Demande",
      tag: "magenta",
      caseListTable: {
        columns: [
          { headerLabel: "ID", headlessWidgetCode: "caseId", width: 1, sorter: true },
          { headerLabel: "Date", headlessWidgetCode: "caseDate", width: 1, sorter: true },
          { headerLabel: "Montant", headlessWidgetCode: "caseAmount", width: 1, sorter: true },
          { headerLabel: "Étape", headlessWidgetCode: "caseEtape", width: 1 },
          { headerLabel: "Sous-Étape", headlessWidgetCode: "caseSousEtape", width: 1 },
          { headerLabel: "Région", headlessWidgetCode: "caseRegion", width: 1 },
          { headerLabel: "Dernier Score", headlessWidgetCode: "caseLastScore", width: 1 },
          {
            headerLabel: "Représentant légal",
            headlessWidgetCode: "legalRepresentative",
            width: 1,
          },
          { headerLabel: "Expert Comptable", headlessWidgetCode: "accountant", width: 1 },
          { headerLabel: "Entreprise", headlessWidgetCode: "company", width: 1 },
        ],
      },
    },
  ],
  customerListTableDefinitions: {
    columns: [
      { headerLabel: "ID RET", headlessWidgetCode: "customer-idRet" },
      { headerLabel: "Entreprise", headlessWidgetCode: "customer-company-name" },
      { headerLabel: "SIREN", headlessWidgetCode: "customer-siren", width: 1, sorter: true },
      {
        headerLabel: "Représentant légal",
        headlessWidgetCode: "customer-legal-rep",
      },

      {
        headerLabel: "Alertes ouvertes",
        headlessWidgetCode: "customer-open-alerts-count",
        width: 1,
      },

      { headerLabel: "Dernière modif.", headlessWidgetCode: "customer-last-modified", width: 1 },
    ],
    searchPlaceholder: undefined,
  },

  defaultAlertVersion: "current",
  dashboardDefinitions: [],
  widgetDefinitions: [],
  viewDefinitions: [],
  propertyDefinitions: [],
  propertyTypesDefinitions: [],
  graph: {
    mappers: {
      nodes: [],
      icons: {
        elements: [],
        identifiers: [],
      },
    },
    getIcon: () => undefined,
    getElementView: () => undefined,
  },
  alertList: {
    columns: [
      { headerLabel: "Type", headlessWidgetCode: "caseType", width: 1 },
      {
        headerLabel: "Date",
        headlessWidgetCode: "alertCreationDate",
        width: 1,
        sorter: true,
        sortBy: "Creation",
      },
      {
        headerLabel: "Nombre de cas",
        headlessWidgetCode: "nbrOfIdentifiedCases",
        width: 1,
        sorter: true,
        sortBy: "NbrOfIdentifiedCases",
      },
      {
        headerLabel: "Score",
        headlessWidgetCode: "alertScore",
        width: 1,
        sorter: true,
        sortBy: "Score",
      },
      { headerLabel: "Scénario", headlessWidgetCode: "alertScenario", width: 1 },
      { headerLabel: "Assigné à", headlessWidgetCode: "alertAssignee", width: 1 },
      { headerLabel: "Décision", headlessWidgetCode: "alertDecision", width: 1 },
      { headerLabel: "Représentant légal", headlessWidgetCode: "legalRepresentative", width: 1 },
    ],
    searchPlaceholder: undefined,
  },
  headlessWidgets: [],
  blacklists: [],
};

const DynamicViewSchemaContext = createContext<DynamicViewSchema>(
  DynamicViewSchemaContextInitialState
);

export const useDynamicViewSchema = () => useContext(DynamicViewSchemaContext);

const DynamicViewSchemaProvider: FC<{ children: ReactNode }> = ({ children }) => {
  const t = useT();
  const { uiSchema, loading, error } = useGetUISchema();

  const contextValue = useMemo<DynamicViewSchemaContextType>(() => {
    if (!uiSchema) return DynamicViewSchemaContextInitialState;

    const getIcon = (code: string, type: "identifier" | "element") => {
      const icons =
        type === "identifier"
          ? uiSchema.graph.mappers.icons.identifiers
          : uiSchema.graph.mappers.icons.elements;
      const icon = icons.find((icon) => icon.code === code);
      return icon?.icon;
    };

    const getElementView = (code: string) => {
      return uiSchema.views.find((view) => {
        const elementViewCode = uiSchema.graph.mappers.nodes.find(
          (element) => element.code === code
        )?.view;
        if (!elementViewCode) return false;
        return view.code === elementViewCode;
      })?.view;
    };

    const mergedCaseTypeDefinitions = (uiSchema.caseTypes ?? []).map((schemaCaseType) => {
      const initialCaseType = DynamicViewSchemaContextInitialState.caseTypeDefinitions.find(
        (initial) => initial.code === schemaCaseType.code
      );

      const caseListTable = schemaCaseType.caseListTable ?? initialCaseType?.caseListTable;

      if (!schemaCaseType.caseListTable && initialCaseType?.caseListTable && import.meta.env.DEV) {
        console.warn(
          ` No caseListTable configuration found for case type "${schemaCaseType.code}". Using default columns from initial state for backward compatibility.`
        );
      }

      return {
        code: schemaCaseType.code ?? initialCaseType?.code ?? "",
        label: schemaCaseType.label ?? initialCaseType?.label ?? schemaCaseType.code ?? "",
        tag: schemaCaseType.tag ?? initialCaseType?.tag ?? "magenta",
        caseListTable,
      };
    });

    const additionalCaseTypes = DynamicViewSchemaContextInitialState.caseTypeDefinitions.filter(
      (initial) => !(uiSchema.caseTypes ?? []).some((schema) => schema.code === initial.code)
    );
    const customerListTable = uiSchema.customerListTable;
    return {
      alertPdfDefinition: { ...uiSchema.alertPdf },
      dashboardDefinitions:
        uiSchema.dashboards ?? DynamicViewSchemaContextInitialState.dashboardDefinitions,
      caseTypeDefinitions: [...mergedCaseTypeDefinitions, ...additionalCaseTypes],
      customerListTableDefinitions: customerListTable,
      widgetDefinitions: uiSchema.widgets ?? DynamicViewSchemaContextInitialState.widgetDefinitions,
      viewDefinitions: uiSchema.views ?? DynamicViewSchemaContextInitialState.viewDefinitions,
      propertyDefinitions:
        uiSchema.properties ?? DynamicViewSchemaContextInitialState.propertyDefinitions,
      propertyTypesDefinitions:
        uiSchema.propertyTypes ?? DynamicViewSchemaContextInitialState.propertyTypesDefinitions,
      defaultAlertVersion:
        uiSchema.defaultAlertVersion ?? DynamicViewSchemaContextInitialState.defaultAlertVersion,
      graph: {
        ...uiSchema.graph,
        getIcon,
        getElementView,
      },
      alertList: uiSchema.alertList ?? DynamicViewSchemaContextInitialState.alertList,
      headlessWidgets:
        uiSchema.headlessWidgets ?? DynamicViewSchemaContextInitialState.headlessWidgets,
      blacklists: uiSchema.blacklists,
    };
  }, [uiSchema]);

  if (error) {
    return <InlineError title={t("ui.schema.error.title")} message={error.message} />;
  }

  if (loading) {
    return <Loading />;
  }

  return (
    <DynamicViewSchemaContext.Provider value={contextValue}>
      {children}
    </DynamicViewSchemaContext.Provider>
  );
};

export default DynamicViewSchemaProvider;
